import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveIcon from '@mui/icons-material/Save';
import {
  Alert,
  Box,
  Button,
  Container,
  DialogActions,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { GridColDef, GridRenderCellParams, GridRowModesModel } from '@mui/x-data-grid';
import { DataGrid, GridRowModes } from '@mui/x-data-grid';

import type { components, definitions, operations } from '@luxuryescapes/contract-svc-promo';

import GlobalScopedMultiSelect from '~/components/Common/Elements/GlobalScopedMultiSelect';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import VoucherConditionValueEditCell from '~/components/Vouchers/VoucherConditionValueEditCell';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import {
  type ChannelMarkupV2Internal,
  checkChannelMarkupV2,
  createOrUpdateChannelMarkupV2,
} from '~/services/PromoService';

/**
 * (moving to contract type too)
 */
type Condition = {
  id: string;
  condition_type: 'offerId' | 'loyaltyOfferType' | 'siteLocation' | 'subscriberTier' | 'userRoleCondition';
  condition_apply_type: 'MUST-INCLUDE' | 'MUST-EXCLUDE';
  condition_value: string;
  specificMarkupIds: Array<string>;
};

type Markup = {
  id: string;
  markup_type: 'fixed' | 'percentage';
  markup_value: string;
  lux_plus_markup_value: string;
  channelMarkupCTAText: string;
  region: string;
  currency: string;
};

type FormValues = {
  conditions: Array<Condition>;
  markups: Array<Markup>;
};

type CheckState = {
  canSave: boolean;
  warnings: Array<string>;
  isChecking: boolean;
};

type RowSaveState = 'saved';

const CHANNEL_MARKUP_CONDITION_TYPE_CONFIG = new Map(
  [
    {
      conditionType: 'loyaltyOfferType',
      name: 'Loyalty Offer Type',
      suggestedConditionValues: [],
    },
  ].map((config) => [config.conditionType, config] as const),
);

const defaultCondition = (): Condition => ({
  id: crypto.randomUUID(),
  condition_type: 'loyaltyOfferType',
  condition_apply_type: 'MUST-INCLUDE',
  condition_value:
    CHANNEL_MARKUP_CONDITION_TYPE_CONFIG.get('loyaltyOfferType')?.suggestedConditionValues.join(',') ?? '',
  specificMarkupIds: [],
});

const defaultMarkup = (): Markup => ({
  id: crypto.randomUUID(),
  markup_type: 'percentage',
  markup_value: '',
  lux_plus_markup_value: '',
  channelMarkupCTAText: '',
  region: '',
  currency: '',
});

const toFormCondition = (
  condition: NonNullable<NonNullable<ChannelMarkupV2Internal['rules']>['conditions']>[number],
): Condition => ({
  id: condition.conditionId ?? crypto.randomUUID(),
  condition_type: condition.conditionType,
  condition_apply_type: condition.conditionApplyType,
  condition_value: condition.conditionValue,
  specificMarkupIds: condition.specificMarkupIds ?? [],
});

const toFormMarkup = (
  markup: NonNullable<NonNullable<ChannelMarkupV2Internal['rules']>['markups']>[number],
): Markup => ({
  id: markup.markupId ?? crypto.randomUUID(),
  markup_type: markup.markupType,
  markup_value: markup.markupValue?.toString() ?? '',
  lux_plus_markup_value: markup.luxPlusMarkupValue?.toString() ?? '',
  channelMarkupCTAText: markup.channelMarkupCTAText ?? '',
  region: markup.region ?? '',
  currency: markup.currency ?? '',
});

const toNumberOrUndefined = (value: string): number | undefined => {
  if (value.trim() === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

type PromoBrand = components['schemas']['Promo Brands'];

const buildChannelMarkupRulesPayload = (conditions: Array<Condition>, markups: Array<Markup>) => ({
  conditions: conditions.map((condition) => ({
    conditionId: crypto.randomUUID(),
    specificMarkupIds: condition.specificMarkupIds.length > 0 ? condition.specificMarkupIds : null,
    conditionType: condition.condition_type,
    conditionApplyType: condition.condition_apply_type,
    conditionValue: condition.condition_value,
  })),
  markups: markups.map((markup) => ({
    markupId: crypto.randomUUID(),
    markupType: markup.markup_type,
    markupValue: toNumberOrUndefined(markup.markup_value),
    luxPlusMarkupValue: toNumberOrUndefined(markup.lux_plus_markup_value),
    ...(markup.channelMarkupCTAText.trim() ? { channelMarkupCTAText: markup.channelMarkupCTAText.trim() } : {}),
    ...(markup.region.trim() ? { region: markup.region.trim() as definitions['Promo Regions'] } : {}),
    ...(markup.currency.trim() ? { currency: markup.currency.trim() as definitions['Promo Currencies'] } : {}),
  })),
});

const buildCreateChannelMarkupPayload = (
  tenantBrand: PromoBrand,
  conditions: Array<Condition>,
  markups: Array<Markup>,
): operations['createChannelMarkupV2']['requestBody']['content']['application/json']['channelMarkup'] => ({
  brand: tenantBrand,
  rules: buildChannelMarkupRulesPayload(conditions, markups),
});

const buildUpdateChannelMarkupPayload = (
  channelMarkupId: string,
  tenantBrand: PromoBrand,
  conditions: Array<Condition>,
  markups: Array<Markup>,
): operations['updateChannelMarkupV2']['requestBody']['content']['application/json']['channelMarkup'] => ({
  channelMarkupId,
  brand: tenantBrand,
  rules: buildChannelMarkupRulesPayload(conditions, markups),
});

const extractWarnings = (response: unknown): Array<string> => {
  if (typeof response !== 'object' || response === null) {
    return [];
  }

  const candidate = response as {
    warnings?: Array<{ message?: string } | string>;
    result?: { warnings?: Array<{ message?: string } | string> };
  };

  const warnings = candidate.result?.warnings ?? candidate.warnings ?? [];
  return warnings
    .map((warning) => (typeof warning === 'string' ? warning : warning.message))
    .filter((warning): warning is string => Boolean(warning));
};

const formatMarkupValue = (markup: Markup) => {
  if (markup.markup_value.trim() === '') {
    return '';
  }

  return markup.markup_type === 'fixed' ? `$${markup.markup_value}` : `${markup.markup_value}%`;
};

const conditionTypeOptions = [
  {
    conditionType: 'loyaltyOfferType',
    name: 'Loyalty Offer Type',
  },
].map((config) => ({
  label: config.name,
  value: config.conditionType,
}));

const conditionColumns = (
  markups: Array<Markup>,
  onRemove: (id: string) => void,
  onUpdate: (id: string, patch: Partial<Condition>) => void,
  showDetails: boolean,
): Array<GridColDef<Condition>> => [
  {
    field: 'condition_type',
    headerName: 'Condition Type',
    flex: 1,
    renderEditCell: (params) => (
      <TextField
        select
        autoFocus
        fullWidth
        value={params.value ?? ''}
        onChange={(event) => {
          const conditionType = event.target.value as Condition['condition_type'];
          params.api.setEditCellValue({ id: params.id, field: params.field, value: conditionType });
          onUpdate(params.id as string, { condition_type: conditionType });
        }}
        helperText={showDetails ? 'Select what kind of condition this rule uses.' : undefined}
      >
        {conditionTypeOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'condition_apply_type',
    headerName: 'Apply Type',
    flex: 1,
    renderEditCell: (params) => (
      <TextField
        select
        autoFocus
        fullWidth
        value={params.value ?? ''}
        onChange={(event) => {
          const conditionApplyType = event.target.value as Condition['condition_apply_type'];
          params.api.setEditCellValue({ id: params.id, field: params.field, value: conditionApplyType });
          onUpdate(params.id as string, {
            condition_apply_type: conditionApplyType,
          });
        }}
        helperText={showDetails ? 'Use MUST-INCLUDE to require the value or MUST-EXCLUDE to block it.' : undefined}
      >
        <MenuItem value="MUST-INCLUDE">MUST-INCLUDE</MenuItem>
        <MenuItem value="MUST-EXCLUDE">MUST-EXCLUDE</MenuItem>
      </TextField>
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'condition_value',
    headerName: 'Condition Value',
    flex: 1.2,
    renderEditCell: (params) => (
      <VoucherConditionValueEditCell
        {...params}
        isDatetime={false}
        suggestedConditionValues={
          params.row.condition_type === 'loyaltyOfferType'
            ? [...(CHANNEL_MARKUP_CONDITION_TYPE_CONFIG.get('loyaltyOfferType')?.suggestedConditionValues ?? [])]
            : []
        }
      />
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'specificMarkupIds',
    headerName: 'Specific Markups',
    flex: 1.3,
    renderCell: (params: GridRenderCellParams<Condition, Array<string>>) => {
      const selectedMarkupIds = params.row.specificMarkupIds;

      if (!selectedMarkupIds.length) {
        return <Typography sx={{ fontWeight: 600 }}>Applies to all markups</Typography>;
      }

      return selectedMarkupIds.join(', ');
    },
    renderEditCell: (params) => (
      <GlobalScopedMultiSelect
        value={params.value ?? []}
        globalLabel="Applies to all markups"
        hideWhenGlobal
        options={markups.map((markup) => ({
          value: markup.id,
          label: (
            <>
              {markup.markup_type} {markup.markup_value || markup.lux_plus_markup_value ? '' : '(empty)'}
            </>
          ),
        }))}
        onChange={(specificMarkupIds) => {
          params.api.setEditCellValue({ id: params.id, field: params.field, value: specificMarkupIds });
          onUpdate(params.id as string, { specificMarkupIds });
        }}
      />
    ),
    editable: true,
    sortable: false,
    filterable: false,
    width: 260,
  },
];

const markupColumns = (showDetails: boolean): Array<GridColDef<Markup>> => [
  {
    field: 'markup_type',
    headerName: 'Markup Type',
    flex: 1,
    renderEditCell: (params) => (
      <TextField
        select
        autoFocus
        fullWidth
        value={params.row.markup_type}
        onChange={(event) =>
          params.api.setEditCellValue({ id: params.id, field: 'markup_type', value: event.target.value })
        }
        helperText={
          showDetails ? 'Choose percentage for a relative adjustment or fixed for an absolute amount.' : undefined
        }
      >
        <MenuItem value="percentage">percentage</MenuItem>
        <MenuItem value="fixed">fixed</MenuItem>
      </TextField>
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'markup_value',
    headerName: 'Markup Value',
    flex: 1,
    renderCell: (params) => formatMarkupValue(params.row),
    renderEditCell: (params) => (
      <Stack>
        <TextField
          autoFocus
          fullWidth
          value={params.row.lux_plus_markup_value}
          onChange={(event) =>
            params.api.setEditCellValue({ id: params.id, field: 'lux_plus_markup_value', value: event.target.value })
          }
          helperText={showDetails ? 'Optional override for LuxPlus bookings using the same markup type.' : undefined}
        />
        <TextField
          autoFocus
          fullWidth
          value={params.row.markup_value}
          onChange={(event) =>
            params.api.setEditCellValue({ id: params.id, field: 'markup_value', value: event.target.value })
          }
          helperText={showDetails ? 'Enter the main markup value for this rule.' : undefined}
        />
      </Stack>
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'channelMarkupCTAText',
    headerName: 'CTA Text',
    flex: 1,
    renderEditCell: (params) => (
      <TextField
        autoFocus
        fullWidth
        value={params.row.channelMarkupCTAText}
        onChange={(event) =>
          params.api.setEditCellValue({ id: params.id, field: 'channelMarkupCTAText', value: event.target.value })
        }
        helperText={showDetails ? 'Optional CTA text shown for this markup.' : undefined}
      />
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'region',
    headerName: 'Region',
    flex: 0.8,
    renderCell: (params) => params.row.region || 'All regions',
    renderEditCell: (params) => (
      <TextField
        autoFocus
        fullWidth
        value={params.row.region}
        onChange={(event) =>
          params.api.setEditCellValue({ id: params.id, field: params.field, value: event.target.value })
        }
        helperText={showDetails ? 'Optional region scope for this markup.' : undefined}
      />
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'currency',
    headerName: 'Currency',
    flex: 0.8,
    renderCell: (params) => params.row.currency || 'Any',
    renderEditCell: (params) => (
      <TextField
        autoFocus
        fullWidth
        value={params.row.currency}
        onChange={(event) =>
          params.api.setEditCellValue({ id: params.id, field: params.field, value: event.target.value })
        }
        helperText={showDetails ? 'Optional currency scope for this markup.' : undefined}
      />
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
];

export default function ChannelMarkupCreateV2Page() {
  const { enqueueSnackbar } = useSnackbar();
  const tenant = useCurrentTenant();
  const { push, goBack } = useAppNavigation();
  const { channelMarkupId } = useParams<{ channelMarkupId?: string }>();
  const location = useLocation() as { state?: { channelMarkup?: ChannelMarkupV2Internal } };
  const initialChannelMarkup = location.state?.channelMarkup;
  const isEditMode = Boolean(channelMarkupId);
  const draftChannelMarkupIdRef = useRef<string>(crypto.randomUUID());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [saveWarnings, setSaveWarnings] = useState<Array<string>>([]);
  const [checkState, setCheckState] = useState<CheckState>({
    canSave: false,
    warnings: [],
    isChecking: false,
  });
  const [checkRefreshVersion, setCheckRefreshVersion] = useState(0);
  const [conditions, setConditions] = useState<Array<Condition>>([defaultCondition()]);
  const [markups, setMarkups] = useState<Array<Markup>>([defaultMarkup()]);
  const [conditionRowModesModel, setConditionRowModesModel] = useState<GridRowModesModel>({});
  const [markupRowModesModel, setMarkupRowModesModel] = useState<GridRowModesModel>({});
  const [conditionRowSaveState, setConditionRowSaveState] = useState<Record<string, RowSaveState>>({});
  const [markupRowSaveState, setMarkupRowSaveState] = useState<Record<string, RowSaveState>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { handleSubmit, reset } = useForm<FormValues>();
  const canEditRows =
    Object.values(conditionRowModesModel).every((rowMode) => rowMode.mode !== GridRowModes.Edit) &&
    Object.values(markupRowModesModel).every((rowMode) => rowMode.mode !== GridRowModes.Edit);

  const createPayload = useMemo(
    () => buildCreateChannelMarkupPayload(tenant.tenant.brand as PromoBrand, conditions, markups),
    [conditions, markups, tenant.tenant.brand],
  );

  const updatePayload = useMemo(
    () =>
      buildUpdateChannelMarkupPayload(
        draftChannelMarkupIdRef.current,
        tenant.tenant.brand as PromoBrand,
        conditions,
        markups,
      ),
    [conditions, markups, tenant.tenant.brand],
  );

  const conditionGridColumns = useMemo(
    () =>
      conditionColumns(
        markups,
        (id) => setConditions((current) => current.filter((row) => row.id !== id)),
        (id, patch) => setConditions((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row))),
        showDetails,
      ),
    [markups, showDetails],
  );

  const markupGridColumns = useMemo(() => markupColumns(showDetails), [showDetails]);

  const markConditionSaved = useCallback((id: string) => {
    setConditionRowSaveState((current) => ({ ...current, [id]: 'saved' }));
    window.setTimeout(() => {
      setConditionRowSaveState((current) => {
        if (current[id] !== 'saved') {
          return current;
        }

        const next = { ...current };
        delete next[id];
        return next;
      });
    }, 1200);
  }, []);

  const markMarkupSaved = useCallback((id: string) => {
    setMarkupRowSaveState((current) => ({ ...current, [id]: 'saved' }));
    window.setTimeout(() => {
      setMarkupRowSaveState((current) => {
        if (current[id] !== 'saved') {
          return current;
        }

        const next = { ...current };
        delete next[id];
        return next;
      });
    }, 1200);
  }, []);

  const conditionGridWithActions = useMemo<Array<GridColDef<Condition>>>(() => {
    return [
      ...conditionGridColumns,
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        width: 120,
        renderCell: (params) => {
          const isEditing = conditionRowModesModel[params.id]?.mode === GridRowModes.Edit;
          return (
            <Stack direction="row" spacing={0.5}>
              {isEditing ? (
                <>
                  <IconButton
                    aria-label="Save condition"
                    size="small"
                    onClick={() => params.api.stopRowEditMode({ id: params.id })}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Cancel condition edit"
                    size="small"
                    onClick={() =>
                      setConditionRowModesModel((current) => ({
                        ...current,
                        [params.id]: { mode: GridRowModes.View, ignoreModifications: true },
                      }))
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {conditionRowSaveState[params.id as string] === 'saved' && (
                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                      Saved
                    </Typography>
                  )}
                  <IconButton
                    aria-label="Edit condition"
                    size="small"
                    onClick={() =>
                      setConditionRowModesModel((current) => ({
                        ...current,
                        [params.id]: { mode: GridRowModes.Edit },
                      }))
                    }
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Remove condition"
                    size="small"
                    onClick={() => {
                      setConditions((current) => current.filter((row) => row.id !== params.id));
                      setHasUnsavedChanges(true);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          );
        },
      },
    ];
  }, [conditionGridColumns, conditionRowModesModel]);

  const markupGridWithActions = useMemo<Array<GridColDef<Markup>>>(() => {
    return [
      ...markupGridColumns,
      {
        field: 'actions',
        headerName: 'Actions',
        sortable: false,
        filterable: false,
        width: 132,
        renderCell: (params) => {
          const isEditing = markupRowModesModel[params.id]?.mode === GridRowModes.Edit;
          return (
            <Stack direction="row" spacing={0.5}>
              {isEditing ? (
                <>
                  <IconButton
                    aria-label="Save markup"
                    size="small"
                    onClick={() => params.api.stopRowEditMode({ id: params.id })}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Cancel markup edit"
                    size="small"
                    onClick={() =>
                      setMarkupRowModesModel((current) => ({
                        ...current,
                        [params.id]: { mode: GridRowModes.View, ignoreModifications: true },
                      }))
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {markupRowSaveState[params.id as string] === 'saved' && (
                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                      Saved
                    </Typography>
                  )}
                  <IconButton
                    aria-label="Edit markup"
                    size="small"
                    onClick={() =>
                      setMarkupRowModesModel((current) => ({
                        ...current,
                        [params.id]: { mode: GridRowModes.Edit },
                      }))
                    }
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Remove markup"
                    size="small"
                    onClick={() => {
                      setMarkups((current) => current.filter((row) => row.id !== params.id));
                      setHasUnsavedChanges(true);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          );
        },
      },
    ];
  }, [markupGridColumns, markupRowModesModel]);

  const runCheck = useCallback(async () => {
    if (!canEditRows || conditions.length === 0 || markups.length === 0) {
      setCheckState({
        canSave: false,
        warnings: [],
        isChecking: false,
      });
      return;
    }

    setCheckState((current) => ({ ...current, isChecking: true }));
    try {
      const response = await checkChannelMarkupV2({
        channelMarkup: isEditMode ? updatePayload : createPayload,
      });

      setCheckState({
        canSave: Boolean(response.result?.canSave),
        warnings: extractWarnings(response),
        isChecking: false,
      });
    } catch (error) {
      setCheckState({
        canSave: false,
        warnings: [],
        isChecking: false,
      });
      enqueueSnackbar(`Failed to validate channel markup: ${error}`, { variant: 'error' });
    }
  }, [canEditRows, conditions.length, createPayload, enqueueSnackbar, isEditMode, markups.length, updatePayload]);

  useEffect(() => {
    if (!canEditRows || conditions.length === 0 || markups.length === 0) {
      setCheckState({
        canSave: false,
        warnings: [],
        isChecking: false,
      });
      return;
    }

    const timeout = window.setTimeout(async () => {
      void runCheck();
    }, 300);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [canEditRows, checkRefreshVersion, conditions.length, markups.length, runCheck]);

  const handleRefreshCheck = useCallback(() => {
    setCheckRefreshVersion((current) => current + 1);
  }, []);

  const handleCreate = handleSubmit(async () => {
    if (!checkState.canSave || checkState.isChecking || !canEditRows) {
      return;
    }

    setIsSubmitting(true);
    setSaveWarnings([]);
    try {
      const response = await createOrUpdateChannelMarkupV2(isEditMode ? updatePayload : createPayload);
      if (response.status < 200 || response.status >= 300) {
        enqueueSnackbar(`Channel Markup v2 ${isEditMode ? 'update' : 'creation'} failed`, { variant: 'error' });
        return;
      }

      const warnings = extractWarnings(response);
      setSaveWarnings(warnings);
      warnings.forEach((warning) => enqueueSnackbar(warning, { variant: 'warning' }));

      reset();
      setConditions([defaultCondition()]);
      setMarkups([defaultMarkup()]);
      setHasUnsavedChanges(false);
      setConditionRowSaveState({});
      setMarkupRowSaveState({});
      enqueueSnackbar(`Channel Markup v2 ${isEditMode ? 'updated' : 'created'} successfully`, {
        variant: 'success',
      });
      push('/marketing/channel-markup');
    } catch (error) {
      enqueueSnackbar(`Channel Markup v2 ${isEditMode ? 'update' : 'creation'} failed: ${error}`, {
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  });

  useEffect(() => {
    if (!initialChannelMarkup) {
      return;
    }

    const initialConditions = initialChannelMarkup.rules?.conditions?.map(toFormCondition) ?? [defaultCondition()];
    const initialMarkups = initialChannelMarkup.rules?.markups?.map(toFormMarkup) ?? [defaultMarkup()];

    const nextChannelMarkupId =
      initialChannelMarkup.channelMarkupId ?? channelMarkupId ?? draftChannelMarkupIdRef.current;
    draftChannelMarkupIdRef.current = String(nextChannelMarkupId);
    setConditions(initialConditions.length > 0 ? initialConditions : [defaultCondition()]);
    setMarkups(initialMarkups.length > 0 ? initialMarkups : [defaultMarkup()]);
    setHasUnsavedChanges(false);
    setConditionRowSaveState({});
    setMarkupRowSaveState({});
  }, [channelMarkupId, initialChannelMarkup]);

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Channel Markup v2 | {isEditMode ? 'Edit' : 'Create'}</title>
      </Helmet>

      <PageSubheader title={isEditMode ? 'Edit Channel Markup v2' : 'Create Channel Markup v2'}>
        <Button variant="text" onClick={() => goBack()}>
          Back to list
        </Button>
      </PageSubheader>

      <Box component="form" onSubmit={handleCreate} mt={2}>
        <Stack spacing={3} maxWidth={1200}>
          {saveWarnings.length > 0 && (
            <Alert severity="warning" variant="outlined">
              <Stack spacing={0.5} component="ul" sx={{ m: 0, pl: 2 }}>
                {saveWarnings.map((warning, index) => (
                  <li key={`${warning}-${index}`}>
                    <Typography variant="body2">{warning}</Typography>
                  </li>
                ))}
              </Stack>
            </Alert>
          )}

          {!checkState.canSave && !checkState.isChecking && (
            <Alert severity="warning" variant="outlined">
              <Stack spacing={1}>
                <Stack spacing={0.5} component="ul" sx={{ m: 0, pl: 2 }}>
                  {checkState.warnings.length > 0 ? (
                    checkState.warnings.map((warning, index) => (
                      <li key={`${warning}-${index}`}>
                        <Typography variant="body2">{warning}</Typography>
                      </li>
                    ))
                  ) : (
                    <li>
                      <Typography variant="body2">
                        Channel markup cannot be saved yet. Refresh the check after making changes.
                      </Typography>
                    </li>
                  )}
                </Stack>
                <Box>
                  <Button variant="text" onClick={handleRefreshCheck} disabled={checkState.isChecking || isSubmitting}>
                    Refresh check
                  </Button>
                </Box>
              </Stack>
            </Alert>
          )}

          {showDetails && (
            <Alert severity="info" variant="outlined">
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Channel markup details</Typography>
                <Typography variant="body2">
                  Markups control the actual pricing adjustment. Conditions decide when a markup applies. Edit a row to
                  see field-level guidance.
                </Typography>
              </Stack>
            </Alert>
          )}

          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">Markups</Typography>
            </Stack>
            <DataGrid
              autoHeight
              hideFooter
              disableRowSelectionOnClick
              rows={markups}
              columns={markupGridWithActions}
              editMode="row"
              rowModesModel={markupRowModesModel}
              onRowModesModelChange={setMarkupRowModesModel}
              processRowUpdate={(updatedRow) => {
                setMarkups((current) =>
                  current.map((row) => (row.id === updatedRow.id ? { ...row, ...updatedRow } : row)),
                );
                setHasUnsavedChanges(true);
                markMarkupSaved(updatedRow.id);
                return updatedRow;
              }}
              onProcessRowUpdateError={(error) => {
                enqueueSnackbar(`Failed to update markup row: ${error}`, { variant: 'error' });
              }}
              sx={{ '& .MuiDataGrid-cell': { alignItems: 'flex-start', py: 1 } }}
            />
            <Button
              startIcon={<AddIcon />}
              variant="text"
              onClick={() => {
                setMarkups((current) => current.concat(defaultMarkup()));
                setHasUnsavedChanges(true);
              }}
            >
              Add markup
            </Button>
          </Stack>

          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">Conditions</Typography>
            </Stack>
            <DataGrid
              autoHeight
              hideFooter
              disableRowSelectionOnClick
              rows={conditions}
              columns={conditionGridWithActions}
              editMode="row"
              rowModesModel={conditionRowModesModel}
              onRowModesModelChange={setConditionRowModesModel}
              processRowUpdate={(updatedRow) => {
                setConditions((current) =>
                  current.map((row) => (row.id === updatedRow.id ? { ...row, ...updatedRow } : row)),
                );
                setHasUnsavedChanges(true);
                markConditionSaved(updatedRow.id);
                return updatedRow;
              }}
              onProcessRowUpdateError={(error) => {
                enqueueSnackbar(`Failed to update condition row: ${error}`, { variant: 'error' });
              }}
              sx={{ '& .MuiDataGrid-cell': { alignItems: 'flex-start', py: 1 } }}
            />
            <Button
              startIcon={<AddIcon />}
              variant="text"
              onClick={() => {
                setConditions((current) => current.concat(defaultCondition()));
                setHasUnsavedChanges(true);
              }}
            >
              Add condition
            </Button>
          </Stack>
        </Stack>

        <DialogActions sx={{ px: 0, pt: 3 }}>
          <Button startIcon={<InfoOutlinedIcon />} variant="text" onClick={() => setShowDetails((current) => !current)}>
            {showDetails ? 'Hide details' : 'More details'}
          </Button>
          <Button onClick={() => goBack()}>Cancel</Button>
          {hasUnsavedChanges && (
            <Typography variant="body2" color="warning.main" sx={{ alignSelf: 'center', fontWeight: 600 }}>
              You have unsaved changes
            </Typography>
          )}
          <Button
            variant="contained"
            type="submit"
            disabled={isSubmitting || checkState.isChecking || !checkState.canSave}
          >
            {checkState.isChecking ? 'Checking...' : isEditMode ? 'Update markup' : 'Create markup'}
          </Button>
        </DialogActions>

        <Alert severity="warning" variant="outlined">
          This is in active development - see channel markup v1 for current tested functionailty
        </Alert>
      </Box>
    </Container>
  );
}
